// SYSTEM_ROUTE: /career
import { useRef, useState } from 'react';
import type { Opportunity, OpportunityStage, ResumeProfile, ResumeProfileInput } from '@life-os/app-core';
import { OPPORTUNITY_STAGES, summarizePipeline, skillsByCategory } from '@life-os/app-core';
import { Button, Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const STAGE_TONE: Record<OpportunityStage, ChipTone> = {
  saved: 'muted',
  applied: 'neutral',
  interviewing: 'warn',
  offer: 'primary',
  closed: 'muted',
};

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '0 12px',
  height: 34,
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

function ResumeView({ resume }: { resume: ResumeProfile }) {
  const cats = skillsByCategory(resume.skills);
  const link = (url: string | null) => (url ? (url.startsWith('http') ? url : `https://${url}`) : null);
  return (
    <>
      <Pane title="Professional profile" hint={resume.seniority ?? undefined} action={resume.targetRole ? <Chip tone="primary">{resume.targetRole}</Chip> : undefined}>
        <Stack>
          {resume.summary ? <div style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)', lineHeight: 1.5 }}>{resume.summary}</div> : null}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {([['GitHub', resume.contact.github], ['LinkedIn', resume.contact.linkedin], ['Portfolio', resume.contact.portfolio]] as const).map(([label, url]) =>
              link(url) ? <a key={label} href={link(url)!} target="_blank" rel="noreferrer"><Chip tone="neutral">{label}</Chip></a> : null,
            )}
          </div>
        </Stack>
      </Pane>

      {resume.experience.length > 0 ? (
        <Pane title="Experience" hint={`${resume.experience.length}`}>
          <Stack>
            {resume.experience.map((e, i) => (
              <CompactRow
                key={i}
                title={`${e.role} · ${e.organization}`}
                subtitle={`${e.type ?? ''}${e.startDate ? ` · ${e.startDate.slice(0, 7)}–${e.isCurrent ? 'now' : (e.endDate?.slice(0, 7) ?? '')}` : ''}${e.summary ? ` · ${e.summary}` : ''}`}
                meta={e.isCurrent ? <Chip tone="primary">current</Chip> : undefined}
              />
            ))}
          </Stack>
        </Pane>
      ) : null}

      {resume.skills.length > 0 ? (
        <Pane title="Skills" hint={`${resume.skills.length}`}>
          <Stack>
            {cats.map(({ category, skills }) => (
              <div key={category}>
                <div className="lc-label" style={{ marginBottom: 6 }}>{category}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {skills.map((s, i) => <Chip key={i} tone="muted">{s.name}</Chip>)}
                </div>
              </div>
            ))}
          </Stack>
        </Pane>
      ) : null}

      {resume.projects.length > 0 ? (
        <Pane title="Projects" hint={`${resume.projects.length}`}>
          <Stack>
            {resume.projects.map((p, i) => (
              <CompactRow key={i} title={p.title} subtitle={[p.stack, p.outcome].filter(Boolean).join(' · ') || undefined} meta={p.status ? <Chip tone="neutral">{p.status}</Chip> : undefined} />
            ))}
          </Stack>
        </Pane>
      ) : null}

      {resume.credentials.length > 0 ? (
        <Pane title="Certifications" hint={`${resume.credentials.length}`}>
          <Stack>
            {resume.credentials.map((c, i) => (
              <CompactRow key={i} title={c.title} subtitle={c.issuer ?? undefined} meta={c.type ? <Chip tone="muted">{c.type}</Chip> : undefined} />
            ))}
          </Stack>
        </Pane>
      ) : null}

      {resume.recognitions.length > 0 ? (
        <Pane title="Achievements" hint={`${resume.recognitions.length}`}>
          <Stack>
            {resume.recognitions.map((r, i) => (
              <CompactRow key={i} title={r.title} subtitle={[r.organization, r.summary].filter(Boolean).join(' · ') || undefined} />
            ))}
          </Stack>
        </Pane>
      ) : null}
    </>
  );
}

export function CareerScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const [title, setTitle] = useState('');
  const [org, setOrg] = useState('');
  const [resumeMsg, setResumeMsg] = useState<string | null>(null);
  const resumeFile = useRef<HTMLInputElement>(null);

  const opps = useAsync<readonly Opportunity[]>(() => services.career.list(), [revision]);
  const resume = useAsync<ResumeProfile | undefined>(() => services.academics.getResumeProfile(), [revision]);
  const items = opps.value ?? [];
  const summary = summarizePipeline(items);

  const importResume = async (file: File) => {
    setResumeMsg(null);
    try {
      const raw = JSON.parse(await file.text()) as { profile?: Record<string, unknown> } & Record<string, unknown>;
      const p = raw.profile ?? {};
      const input: ResumeProfileInput = {
        targetRole: (p.targetRole as string) ?? null,
        seniority: (p.seniority as string) ?? null,
        marketFocus: (p.marketFocus as string) ?? null,
        summary: (p.summary as string) ?? null,
        strengths: (p.strengths as string) ?? null,
        gaps: (p.gaps as string) ?? null,
        goals: (p.goals as string) ?? null,
        philosophy: (p.philosophy as string) ?? null,
        lifestyle: (p.lifestyle as string) ?? null,
        contact: {
          email: (p.email as string) ?? null,
          phone: (p.phone as string) ?? null,
          github: (p.github as string) ?? null,
          linkedin: (p.linkedin as string) ?? null,
          portfolio: (p.portfolio as string) ?? null,
        },
        skills: (raw.skills as ResumeProfileInput['skills']) ?? [],
        experience: (raw.experience as ResumeProfileInput['experience']) ?? [],
        projects: (raw.projects as ResumeProfileInput['projects']) ?? [],
        credentials: (raw.credentials as ResumeProfileInput['credentials']) ?? [],
        recognitions: (raw.recognitions as ResumeProfileInput['recognitions']) ?? [],
      };
      const r = await services.academics.importResumeProfile(input);
      setResumeMsg(`Imported résumé — ${r.experience.length} roles, ${r.skills.length} skills, ${r.projects.length} projects.`);
      refresh();
    } catch (err) {
      setResumeMsg(`Import failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      if (resumeFile.current) resumeFile.current.value = '';
    }
  };

  const add = async () => {
    if (title.trim().length === 0) return;
    await services.career.create({ title, organization: org });
    setTitle('');
    setOrg('');
    refresh();
  };

  return (
    <>
      <ScreenHeader title={<>Career & Identity</>} subtitle={<>Track roles and opportunities through a clear pipeline. Link each to the projects and skills
            that prove your fit. No vanity metrics — just what's in play and the next move.</>} />

      {resume.value ? <ResumeView resume={resume.value} /> : null}

      <Pane title="Résumé" hint={resume.value ? 'Imported · re-import to update' : 'Import your living CV'}>
        <Stack>
          <div style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>
            Load <code>resume-v2.json</code> (migrated from v1): profile, experience, skills, projects,
            certifications, and achievements. Re-importing updates the single résumé record.
          </div>
          <input
            ref={resumeFile}
            type="file"
            accept="application/json,.json"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void importResume(f); }}
            aria-label="Résumé file"
          />
          <div><Button variant="primary" onClick={() => resumeFile.current?.click()}>Import résumé</Button></div>
          {resumeMsg ? <Chip tone="primary">{resumeMsg}</Chip> : null}
        </Stack>
      </Pane>

      <Pane title="Pipeline" hint={`${summary.active} active of ${summary.total}`}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {OPPORTUNITY_STAGES.map((st) => (
            <Chip key={st} tone={summary.byStage[st] > 0 ? STAGE_TONE[st] : 'muted'}>
              {st} {summary.byStage[st] > 0 ? summary.byStage[st] : ''}
            </Chip>
          ))}
        </div>
      </Pane>

      <Pane title="Add an opportunity">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 200px' }}
            value={title}
            placeholder="Role / opportunity…"
            onChange={(e) => setTitle(e.target.value)}
            aria-label="Opportunity title"
          />
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 160px' }}
            value={org}
            placeholder="Organization"
            onChange={(e) => setOrg(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void add();
            }}
            aria-label="Organization"
          />
          <Button variant="primary" onClick={() => void add()}>
            Add
          </Button>
        </div>
      </Pane>

      {OPPORTUNITY_STAGES.map((stage) => {
        const inStage = items.filter((o) => o.stage === stage);
        if (inStage.length === 0) return null;
        return (
          <Pane key={stage} title={stage[0]!.toUpperCase() + stage.slice(1)} hint={`${inStage.length}`}>
            <Stack>
              {inStage.map((o) => (
                <div key={String(o.id)} className="lc-row" style={{ alignItems: 'center', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <strong>{o.title}</strong>
                    {o.organization ? <span style={{ color: 'var(--lc-on-surface-variant)' }}> · {o.organization}</span> : null}
                  </div>
                  <Chip tone={STAGE_TONE[o.stage]}>{o.stage}</Chip>
                  <select
                    value={o.stage}
                    onChange={(e) => void services.career.setStage(o.id, e.target.value as OpportunityStage).then(refresh)}
                    aria-label="Stage"
                    style={inputStyle}
                  >
                    {OPPORTUNITY_STAGES.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </Stack>
          </Pane>
        );
      })}

      {items.length === 0 ? (
        <Pane title="Opportunities">
          <EmptyState title="No opportunities yet">
            Add a role you're interested in. Move it through the pipeline as things progress.
          </EmptyState>
        </Pane>
      ) : null}
    </>
  );
}
